import { useState, useEffect } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Send, X, MessageCircle } from "lucide-react";

/** Normalise phone -> digits only with India default 91 prefix when 10-digit. */
function normalisePhone(phone) {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) return "91" + digits;
  return digits;
}

/** Download a file from an authenticated backend endpoint. */
export async function downloadAuthFile(path, filename) {
  const token = localStorage.getItem("taxinpho_token");
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/** Dialog: confirm phone + message → tries Cloud API; falls back to wa.me link. */
export function WhatsAppDialog({ open, onClose, defaultPhone, defaultMessage, attachLabel }) {
  const [phone, setPhone] = useState(defaultPhone || "");
  const [message, setMessage] = useState(defaultMessage || "");
  const [configured, setConfigured] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPhone(defaultPhone || "");
    setMessage(defaultMessage || "");
    api.get("/whatsapp/status").then(r => setConfigured(r.data.configured)).catch(() => {});
  }, [open, defaultPhone, defaultMessage]);

  if (!open) return null;

  const sendViaApi = async () => {
    setSending(true);
    try {
      await api.post("/whatsapp/send", { to: normalisePhone(phone), message });
      toast.success("WhatsApp message sent");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed — opening Click-to-Chat as fallback");
      openWaMe();
    } finally {
      setSending(false);
    }
  };

  const openWaMe = () => {
    const num = normalisePhone(phone);
    if (!num) { toast.error("Enter a valid phone number"); return; }
    const url = `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-200 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="whatsapp-dialog">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle size={18} className="text-[#25D366]" />
            <h2 className="font-display font-bold text-xl tracking-tight">Send on WhatsApp</h2>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label-caps block mb-2">Client Phone *</label>
            <input
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="e.g. 9876543210 or +91 9876543210"
              data-testid="wa-phone"
              className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900 font-mono"
            />
            <p className="text-xs text-zinc-500 mt-1">10-digit Indian numbers auto-prefixed with +91.</p>
          </div>
          <div>
            <label className="label-caps block mb-2">Message</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows="6"
              data-testid="wa-message"
              className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900 text-sm"
            />
          </div>
          {attachLabel && (
            <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 text-xs text-zinc-600 flex items-center gap-2">
              <span className="label-caps text-zinc-700">PDF</span>
              <span>{attachLabel}</span>
              <span className="ml-auto text-zinc-500 font-mono">Download first, then attach manually after WhatsApp opens.</span>
            </div>
          )}
          {!configured && (
            <p className="text-xs text-zinc-500 leading-relaxed">
              <strong>Click-to-Chat mode:</strong> opens WhatsApp Web/Desktop with the message pre-filled. To enable auto-send + PDF attachment, add Meta Cloud API keys in <code className="font-mono bg-zinc-100 px-1">backend/.env</code>.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 border border-zinc-300 font-semibold">Cancel</button>
            {configured && (
              <button onClick={sendViaApi} disabled={sending} data-testid="wa-send-api" className="bg-[#25D366] text-white px-4 py-2 font-semibold hover:bg-green-600 disabled:opacity-50 flex items-center gap-2">
                <Send size={14} /> {sending ? "Sending..." : "Send via API"}
              </button>
            )}
            <button onClick={openWaMe} data-testid="wa-open" className="bg-[#25D366] text-white px-4 py-2 font-semibold hover:bg-green-600 flex items-center gap-2">
              <MessageCircle size={14} /> Open WhatsApp
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
